import { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Stack, Typography, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../../services/api';

/* ------------------------------------------------------------------ */
/* Typography: load Fraunces, Plus Jakarta Sans, JetBrains Mono once.  */
/* ------------------------------------------------------------------ */
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';

function ensureFontsLoaded() {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

/* ------------------------------------------------------------------ */
/* Palette                                                             */
/* ------------------------------------------------------------------ */
const INK = '#1a1612';
const INK_SOFT = '#4a4540';
const INK_FAINT = '#8a847d';
const PAPER = '#f4efe6';
const PAPER_RULE = 'rgba(26, 22, 18, 0.14)';
const ACCENT = '#c13a16'; // terracota
const ACCENT_INK = '#7a1f07';
const TEAL = '#244a53';
const SUCCESS = '#3e6e4f';

const SERIF = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const SANS = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, monospace';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */
interface ProviderStatus {
  code: 'mercadopago' | 'stripe';
  label: string;
  connected: boolean;
  accountEmail?: string | null;
  externalAccountId?: string | null;
  connectedAt?: string | null;
  expiresAt?: string | null;
  legacyFallback?: boolean;
}

interface ConnectUrlResponse {
  authUrl: string;
  qrCodeUrl: string;
  state: string;
  instructions: string;
}

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */
const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })
    : '—';

const providerTagline = (code: string) =>
  code === 'mercadopago'
    ? 'Cobros recurrentes en pesos para Argentina, Brasil, México, Chile, Colombia, Perú y Uruguay.'
    : 'Suscripciones recurrentes en USD / EUR para clientes de Estados Unidos, Europa y el resto del mundo.';

/* ------------------------------------------------------------------ */
/* Visual primitives                                                   */
/* ------------------------------------------------------------------ */

const PaperBackdrop = () => (
  <>
    {/* Very subtle printed-paper noise */}
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        opacity: 0.5,
        mixBlendMode: 'multiply',
        backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
          '<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'220\' height=\'220\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\' seed=\'3\' stitchTiles=\'stitch\'/><feColorMatrix type=\'matrix\' values=\'0 0 0 0 0.08  0 0 0 0 0.06  0 0 0 0 0.04  0 0 0 0.08 0\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/></svg>'
        )}")`,
      }}
    />
    {/* Vignette so the edges feel like real paper */}
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        background: `radial-gradient(120% 85% at 50% 40%, transparent 55%, rgba(26, 22, 18, 0.08))`,
      }}
    />
  </>
);

const Kicker = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="p"
    sx={{
      fontFamily: MONO,
      fontSize: 11,
      letterSpacing: '0.28em',
      textTransform: 'uppercase',
      color: INK_FAINT,
    }}
  >
    {children}
  </Typography>
);

const HairlineRule = ({ label }: { label?: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
    <Box sx={{ flex: 1, height: '1px', background: PAPER_RULE }} />
    {label && (
      <Typography sx={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: INK_FAINT, textTransform: 'uppercase' }}>
        {label}
      </Typography>
    )}
    <Box sx={{ flex: 1, height: '1px', background: PAPER_RULE }} />
  </Box>
);

const StatusBadge = ({ connected }: { connected: boolean }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      px: 1.2,
      py: 0.35,
      border: `1px solid ${connected ? SUCCESS : PAPER_RULE}`,
      background: connected ? 'rgba(62, 110, 79, 0.08)' : 'transparent',
      borderRadius: '2px',
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: connected ? SUCCESS : INK_FAINT,
        boxShadow: connected ? `0 0 0 3px rgba(62, 110, 79, 0.16)` : 'none',
      }}
    />
    <Typography
      sx={{
        fontFamily: MONO,
        fontSize: 10.5,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: connected ? SUCCESS : INK_FAINT,
        fontWeight: 500,
      }}
    >
      {connected ? 'Autorizado' : 'Sin vincular'}
    </Typography>
  </Box>
);

const InkButton = ({
  onClick,
  children,
  variant = 'primary',
  disabled,
  endIcon,
}: {
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'ghost';
  disabled?: boolean;
  endIcon?: React.ReactNode;
}) => {
  const isPrimary = variant === 'primary';
  return (
    <Button
      disableRipple
      disableElevation
      onClick={onClick}
      disabled={disabled}
      endIcon={endIcon}
      sx={{
        fontFamily: MONO,
        fontSize: 11,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        px: isPrimary ? 3 : 2,
        py: 1.2,
        borderRadius: 0,
        minHeight: 40,
        color: isPrimary ? PAPER : INK,
        background: isPrimary ? INK : 'transparent',
        border: isPrimary ? `1px solid ${INK}` : `1px solid ${PAPER_RULE}`,
        transition: 'all 180ms ease',
        '&:hover': {
          background: isPrimary ? ACCENT_INK : 'rgba(26, 22, 18, 0.04)',
          borderColor: isPrimary ? ACCENT_INK : INK,
          color: isPrimary ? PAPER : INK,
        },
        '&.Mui-disabled': { opacity: 0.35, color: INK },
      }}
    >
      {children}
    </Button>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

type ViewState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'error'; msg: string };

export default function Payments() {
  ensureFontsLoaded();

  const [providers, setProviders] = useState<ProviderStatus[]>([]);
  const [view, setView] = useState<ViewState>({ kind: 'loading' });
  const [banner, setBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const [connectDialog, setConnectDialog] = useState<null | { provider: ProviderStatus; data: ConnectUrlResponse }>(null);
  const [generatingConnect, setGeneratingConnect] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);

  const api = useMemo(() => (apiService as any).api, []);

  useEffect(() => {
    // Flash banner from OAuth callback (`?mp=connected` or `?mp=error`).
    const p = new URLSearchParams(window.location.search);
    if (p.get('mp') === 'connected') setBanner({ tone: 'success', text: 'Mercado Pago quedó vinculado. Los cobros de suscripciones van a entrar a esta cuenta.' });
    if (p.get('mp') === 'error') setBanner({ tone: 'error', text: 'No pudimos completar la vinculación con Mercado Pago. Probá de nuevo.' });
    if (p.get('mp')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('mp');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const load = async () => {
    try {
      setView({ kind: 'loading' });
      const res = await api.get('/super-admin/payments/providers');
      setProviders(res.data);
      setView({ kind: 'idle' });
    } catch (e: any) {
      setView({ kind: 'error', msg: e?.response?.data?.error || 'No pudimos cargar los proveedores' });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openConnectDialog = async (provider: ProviderStatus) => {
    if (provider.code === 'stripe') {
      setBanner({ tone: 'error', text: 'Stripe Connect se habilita cuando vincules tu cuenta Stripe desde el panel de integraciones. Próximamente.' });
      return;
    }
    setGeneratingConnect(provider.code);
    try {
      const res = await api.get(`/super-admin/payments/${provider.code}/connect-url`);
      setConnectDialog({ provider, data: res.data });
    } catch (e: any) {
      setBanner({ tone: 'error', text: e?.response?.data?.error || 'No pudimos generar el link de vinculación' });
    } finally {
      setGeneratingConnect(null);
    }
  };

  const disconnect = async (code: string) => {
    if (!window.confirm(`¿Desvincular ${code}? Los cobros nuevos van a fallar hasta que vincules otra cuenta.`)) return;
    setDisconnecting(code);
    try {
      await api.post(`/super-admin/payments/${code}/disconnect`);
      await load();
      setBanner({ tone: 'success', text: `${code === 'mercadopago' ? 'Mercado Pago' : 'Stripe'} desvinculado.` });
    } catch (e: any) {
      setBanner({ tone: 'error', text: e?.response?.data?.error || 'No pudimos desvincular.' });
    } finally {
      setDisconnecting(null);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: PAPER,
        color: INK,
        fontFamily: SANS,
        overflowX: 'hidden',
      }}
    >
      <PaperBackdrop />

      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1120, mx: 'auto', px: { xs: 3, md: 6 }, py: { xs: 6, md: 10 } }}>

        {/* ============ HEADER / MASTHEAD ============ */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
            <Kicker>Cuenta de cobro · Plataforma</Kicker>
            <Kicker>N.º 2026 — Payments</Kicker>
          </Stack>

          <Typography
            component="h1"
            sx={{
              fontFamily: SERIF,
              fontWeight: 350,
              fontVariationSettings: `"opsz" 144, "SOFT" 40`,
              fontSize: { xs: 54, sm: 80, md: 104 },
              lineHeight: 0.95,
              letterSpacing: '-0.035em',
              color: INK,
              mb: 2.5,
            }}
          >
            Dónde cae el <em style={{ fontStyle: 'italic', color: ACCENT, fontWeight: 360 }}>dinero</em><br />
            de tus clientes.
          </Typography>

          <Box sx={{ width: 110, height: '2px', background: INK, mb: 3.5 }} />

          <Typography
            component="p"
            sx={{
              fontFamily: SERIF,
              fontSize: { xs: 17, md: 20 },
              lineHeight: 1.55,
              color: INK_SOFT,
              maxWidth: 640,
              fontWeight: 350,
            }}
          >
            Vinculá una cuenta de Mercado Pago o Stripe para que las suscripciones que tus tenants
            paguen mes a mes se acrediten directo ahí. Se configura una sola vez.
            Podés cambiarla o desvincularla cuando quieras.
          </Typography>
        </motion.div>

        {banner && (
          <Box sx={{ mt: 5 }}>
            <Alert
              severity={banner.tone}
              onClose={() => setBanner(null)}
              sx={{
                borderRadius: 0,
                border: `1px solid ${banner.tone === 'success' ? SUCCESS : ACCENT}`,
                background: banner.tone === 'success' ? 'rgba(62, 110, 79, 0.08)' : 'rgba(193, 58, 22, 0.06)',
                color: INK,
                fontFamily: SANS,
                '& .MuiAlert-icon': { color: banner.tone === 'success' ? SUCCESS : ACCENT },
              }}
            >
              {banner.text}
            </Alert>
          </Box>
        )}

        <Box sx={{ mt: { xs: 7, md: 10 } }}>
          <HairlineRule label={`${providers.filter(p => p.connected).length} de ${providers.length} activos`} />
        </Box>

        {/* ============ PROVIDER ENTRIES ============ */}
        {view.kind === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={28} sx={{ color: INK }} />
          </Box>
        )}

        {view.kind === 'error' && (
          <Alert severity="error" sx={{ mt: 4, borderRadius: 0, border: `1px solid ${ACCENT}`, background: 'rgba(193, 58, 22, 0.06)' }}>
            {view.msg}
          </Alert>
        )}

        {view.kind === 'idle' && (
          <Box>
            {providers.map((p, idx) => (
              <motion.div
                key={p.code}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 + idx * 0.12, ease: [0.2, 0.7, 0.2, 1] }}
              >
                <ProviderEntry
                  provider={p}
                  index={idx + 1}
                  generating={generatingConnect === p.code}
                  disconnecting={disconnecting === p.code}
                  onConnect={() => openConnectDialog(p)}
                  onDisconnect={() => disconnect(p.code)}
                />
                {idx < providers.length - 1 && <Box sx={{ height: '1px', background: PAPER_RULE }} />}
              </motion.div>
            ))}
          </Box>
        )}

        {/* ============ FOOTNOTE ============ */}
        <Box sx={{ mt: 10, pt: 5, borderTop: `1px solid ${PAPER_RULE}` }}>
          <Typography sx={{ fontFamily: SERIF, fontSize: 13.5, fontStyle: 'italic', color: INK_FAINT, maxWidth: 620, lineHeight: 1.55 }}>
            La plataforma nunca retiene fondos. Las suscripciones cobradas por MP o Stripe entran
            directamente a la cuenta vinculada; nosotros sólo recibimos la notificación de pago
            para activar el tenant correspondiente.
          </Typography>
        </Box>
      </Box>

      {/* ============ CONNECT MODAL ============ */}
      <AnimatePresence>
        {connectDialog && (
          <Dialog
            open
            onClose={() => setConnectDialog(null)}
            maxWidth="md"
            PaperProps={{
              sx: {
                background: PAPER,
                borderRadius: 0,
                border: `1px solid ${INK}`,
                boxShadow: '0 40px 80px rgba(26, 22, 18, 0.22)',
                backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
                  '<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'220\' height=\'220\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\' seed=\'7\' stitchTiles=\'stitch\'/><feColorMatrix type=\'matrix\' values=\'0 0 0 0 0.08  0 0 0 0 0.06  0 0 0 0 0.04  0 0 0 0.07 0\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/></svg>'
                )}")`,
              },
            }}
          >
            <ConnectDialogContent
              provider={connectDialog.provider}
              data={connectDialog.data}
              onClose={() => setConnectDialog(null)}
            />
          </Dialog>
        )}
      </AnimatePresence>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* ProviderEntry — magazine-ledger row                                 */
/* ------------------------------------------------------------------ */
function ProviderEntry({
  provider,
  index,
  generating,
  disconnecting,
  onConnect,
  onDisconnect,
}: {
  provider: ProviderStatus;
  index: number;
  generating: boolean;
  disconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '64px 1fr 280px' },
        columnGap: { xs: 0, md: 6 },
        rowGap: { xs: 3, md: 0 },
        py: { xs: 5, md: 7 },
        alignItems: 'start',
      }}
    >
      {/* Index number */}
      <Box>
        <Typography
          sx={{
            fontFamily: SERIF,
            fontWeight: 300,
            fontVariationSettings: `"opsz" 144, "SOFT" 0`,
            fontSize: { xs: 36, md: 48 },
            lineHeight: 1,
            color: INK_FAINT,
            letterSpacing: '-0.03em',
          }}
        >
          0{index}
        </Typography>
      </Box>

      {/* Main content */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Typography
            sx={{
              fontFamily: SERIF,
              fontWeight: 360,
              fontVariationSettings: `"opsz" 144, "SOFT" 30`,
              fontSize: { xs: 38, md: 52 },
              letterSpacing: '-0.03em',
              lineHeight: 1,
              color: INK,
            }}
          >
            {provider.label}
          </Typography>
          <StatusBadge connected={provider.connected} />
        </Stack>

        <Typography sx={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 15, color: INK_SOFT, maxWidth: 560, mb: 2.5, lineHeight: 1.5 }}>
          {providerTagline(provider.code)}
        </Typography>

        {/* Technical details row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
            rowGap: 2,
            columnGap: 4,
            maxWidth: 540,
            borderTop: `1px solid ${PAPER_RULE}`,
            pt: 2,
          }}
        >
          <DetailCell label="Cuenta" value={provider.accountEmail || '—'} />
          <DetailCell label="ID externo" value={provider.externalAccountId || '—'} mono />
          <DetailCell label="Desde" value={formatDate(provider.connectedAt)} />
        </Box>

        {provider.legacyFallback && (
          <Typography sx={{ mt: 2, fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: '0.08em' }}>
            Hay un token viejo en appsettings.json — vinculá por OAuth para usar refresh automático.
          </Typography>
        )}
      </Box>

      {/* Actions column */}
      <Stack spacing={1.2} sx={{ alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
        {provider.connected ? (
          <>
            <InkButton variant="ghost" onClick={() => (window.location.href = provider.code === 'mercadopago' ? 'https://www.mercadopago.com.ar/developers' : 'https://dashboard.stripe.com')} endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}>
              Ver en {provider.code === 'mercadopago' ? 'MP' : 'Stripe'}
            </InkButton>
            <InkButton variant="ghost" onClick={onDisconnect} disabled={disconnecting}>
              {disconnecting ? 'Desvinculando…' : 'Desvincular'}
            </InkButton>
          </>
        ) : (
          <InkButton onClick={onConnect} disabled={generating}>
            {generating ? 'Generando…' : 'Vincular cuenta'}
          </InkButton>
        )}
      </Stack>
    </Box>
  );
}

function DetailCell({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 9.5,
          letterSpacing: '0.26em',
          textTransform: 'uppercase',
          color: INK_FAINT,
          mb: 0.4,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: mono ? MONO : SANS,
          fontSize: 13.5,
          color: INK,
          fontWeight: mono ? 400 : 500,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Connect dialog — QR ceremony                                        */
/* ------------------------------------------------------------------ */
function ConnectDialogContent({
  provider,
  data,
  onClose,
}: {
  provider: ProviderStatus;
  data: ConnectUrlResponse;
  onClose: () => void;
}) {
  return (
    <Box sx={{ position: 'relative', p: { xs: 4, md: 6 }, color: INK }}>
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 14, right: 14, color: INK, borderRadius: 0 }}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
        <Kicker>Autorización</Kicker>
        <Typography sx={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.28em', color: INK_FAINT, textTransform: 'uppercase' }}>
          · {provider.label}
        </Typography>
      </Stack>

      <Typography
        sx={{
          fontFamily: SERIF,
          fontWeight: 340,
          fontVariationSettings: `"opsz" 144, "SOFT" 40`,
          fontSize: { xs: 40, md: 56 },
          lineHeight: 0.98,
          letterSpacing: '-0.03em',
          color: INK,
          mb: 1,
        }}
      >
        Vinculá tu cuenta<br />
        con un <em style={{ color: ACCENT, fontStyle: 'italic' }}>QR</em>.
      </Typography>

      <Box sx={{ width: 80, height: '2px', background: INK, my: 3 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '340px 1fr' },
          gap: { xs: 3, md: 6 },
          alignItems: 'start',
        }}
      >
        {/* QR Plate */}
        <Box>
          <Box
            sx={{
              position: 'relative',
              p: 2.5,
              background: '#fff',
              border: `1px solid ${INK}`,
              boxShadow: `6px 6px 0 ${INK}`,
            }}
          >
            <Box
              component="img"
              src={data.qrCodeUrl}
              alt="QR de autorización"
              sx={{ width: '100%', height: 'auto', display: 'block' }}
            />
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 9,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: INK_FAINT,
                mt: 1.5,
                textAlign: 'center',
              }}
            >
              State · {data.state.slice(0, 12)}…
            </Typography>
          </Box>
        </Box>

        {/* Instructions */}
        <Box>
          <Stack spacing={2.5}>
            <RitualStep
              n="01"
              title="Abrí Mercado Pago en tu celular"
              body="Cualquier teléfono donde tengas la sesión abierta. No hace falta ser admin de una organización."
            />
            <RitualStep
              n="02"
              title="Escaneá este QR"
              body="Se abre la pantalla de autorización de Mercado Pago. Confirmá que el nombre de la aplicación es el que esperás."
            />
            <RitualStep
              n="03"
              title="Autorizá y volvé"
              body="Te va a redirigir de vuelta acá. La cuenta queda vinculada y los cobros de suscripciones empiezan a caer ahí al instante."
            />
          </Stack>

          <HairlineRule label="o, sin QR" />

          <InkButton onClick={() => window.open(data.authUrl, '_blank', 'noopener')} endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}>
            Abrir en este navegador
          </InkButton>
        </Box>
      </Box>

      <Typography
        sx={{
          mt: 5,
          pt: 3,
          borderTop: `1px solid ${PAPER_RULE}`,
          fontFamily: SERIF,
          fontStyle: 'italic',
          fontSize: 12.5,
          color: INK_FAINT,
          lineHeight: 1.55,
        }}
      >
        La vinculación usa OAuth 2.0. Guardamos tu access-token y un refresh-token cifrados; no se
        expone la contraseña de Mercado Pago, y podés revocar el acceso desde{' '}
        <strong style={{ color: INK, fontStyle: 'normal' }}>
          mercadopago.com.ar → Tu perfil → Aplicaciones conectadas
        </strong>
        .
      </Typography>
    </Box>
  );
}

function RitualStep({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <Stack direction="row" spacing={2.5} alignItems="flex-start">
      <Typography
        sx={{
          fontFamily: SERIF,
          fontWeight: 300,
          fontVariationSettings: `"opsz" 144, "SOFT" 10`,
          fontSize: 28,
          letterSpacing: '-0.02em',
          color: ACCENT,
          lineHeight: 1,
          minWidth: 48,
        }}
      >
        {n}
      </Typography>
      <Box>
        <Typography
          sx={{
            fontFamily: SERIF,
            fontWeight: 500,
            fontSize: 17,
            color: INK,
            mb: 0.4,
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: 13.5, color: INK_SOFT, lineHeight: 1.55 }}>
          {body}
        </Typography>
      </Box>
    </Stack>
  );
}
